import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useLazyQuery, useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { intl } from "LocaleWrapper";

import { Button, Card, FormGroup, PageHeader, Textarea } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import Language from "components/utils/Language";
import Helmet from "components/helmet/Helmet";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import RelationSelect from "view/relations/components/selects/RelationSelect";

import { termsSchema, TermsSchemaInput, TermsSchemaOutput } from "./utils/terms.schema";

import { SaveTermsAndConditionsDocument } from "__generated__/view/terms/graphql/mutations.generated";
import { TermsAndConditionsDocument } from "__generated__/view/terms/graphql/queries.generated";
import { RelationType } from "__generated__/enums";

const TermsAndConditions = () => {
	const navigate = useNavigate();
	const { id } = useParams();

	const [ getTermsAndConditions ] = useLazyQuery(TermsAndConditionsDocument);
	const [ saveTermsAndConditions, { loading } ] = useMutation(SaveTermsAndConditionsDocument);

	const { control, handleSubmit, reset } = useForm<TermsSchemaInput, unknown, TermsSchemaOutput>({
		resolver: zodResolver(termsSchema),
		defaultValues: {
			id: null,
			description: "",
			debtor: null,
		},
	});

	const save = (data: TermsSchemaOutput) => {
		saveTermsAndConditions({
			variables: {
				input: {
					id: data.id,
					description: data.description,
					debtor: { id: data.debtor?.id },
				},
			},
		}).then(() => navigate("/view/terms"));
	};

	useEffect(() => {
		if (id) {
			getTermsAndConditions({
				variables: {
					id: Number(id),
				},
			}).then(({ data }) => {
				reset({
					id: data?.termsAndConditions?.id ?? null,
					description: data?.termsAndConditions?.description ?? "",
					debtor: data?.termsAndConditions?.debtor ?? null,
				});
			});
		}
	}, [ id ]);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("TERMS_AND_CONDITIONS")}
					<Helmet title={intl.formatMessage({ id: "TERMS_AND_CONDITIONS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/terms")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12 }}>
					<Card>
						<FormGroup
							label={Language("DESCRIPTION")}
							labelPlacement="left"
							overflow="hidden"
							required>
							<Controller
								name="description"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Textarea {...field} autoFocus />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup label={Language("CUSTOMER")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="debtor"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<RelationSelect {...field} types={[ RelationType.Customer ]} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default TermsAndConditions;
